// Single source of truth for site copy, imagery and pricing.
// Update BRAND here to rename the company everywhere.
export const BRAND = 'RIME';
export const BRAND_TAGLINE = 'Bookkeeping + Payroll';
export const SITE_URL = 'https://hellorime.com';

export const defaultMeta = {
  title: 'RIME | Bookkeeping + Payroll for Small Businesses',
  description:
    'Expert bookkeeping and payroll for small businesses and independent professionals. Monthly bookkeeping, cleanup, QuickBooks setup, and managed payroll.',
};

export const contact = {
  email: 'contact@hellorime.com',
  phone: '+1 510.982.6565',
  phoneHref: 'tel:+15109826565',
  scheduling: 'calendly.com/hellorime/rime-discovery-call',
  schedulingHref: 'https://calendly.com/hellorime/rime-discovery-call',
  location: 'Serving clients nationwide',
};

export const socialLinks = [
  {
    id: 'linkedin',
    label: 'Rime on LinkedIn',
    href: 'https://www.linkedin.com/company/hellorime',
  },
  {
    id: 'instagram',
    label: 'Rime on Instagram',
    href: 'https://www.instagram.com/hellorime',
  },
];

export const startLinkProps = {
  href: contact.schedulingHref,
  target: '_blank',
  rel: 'noopener noreferrer',
};

export const routes = {
  home: '/',
  privacy: '/privacy',
  terms: '/terms',
};

export const navLinks = [
  { label: 'About', href: '/#about' },
  { label: 'Services', href: '/#services' },
  { label: 'Industries', href: '/#industries' },
  { label: 'Pricing', href: '/#pricing' },
  { label: 'FAQ', href: '/#faq' },
  { label: 'Contact', href: '/#contact' },
];

export const footerLinks = [
  { label: 'About', href: '/#about' },
  { label: 'Services', href: '/#services' },
  { label: 'Industries', href: '/#industries' },
  { label: 'Pricing', href: '/#pricing' },
  { label: 'FAQ', href: '/#faq' },
  { label: 'Contact', href: '/#contact' },
];

export const aboutCredentials = [
  {
    name: 'QBO Level 2 Certified',
    src: '/images/logos/quickbooks-proadvisor.png',
  },
  {
    name: 'Intuit Bookkeeping Certified',
    src: '/images/logos/intuit-bookkeeping.png',
  },
];

export const payrollCredential = {
  name: 'QuickBooks Payroll Certified',
  src: '/images/logos/quickbooks-payroll.png',
};

export const platformLogos = [
  { name: 'ADP Payroll & HR', src: '/images/logos/adp.png' },
  { name: 'Paychex Payroll & HR', src: '/images/logos/paychex.png' },
];

export const images = {
  logo: '/images/brand/rime-lockup.png',
  logoOnDark: '/images/brand/rime-lockup-on-dark.png',
  mark: '/images/brand/rime-mark.png',
  hero: '/images/rime-hero.jpg',
  contact: '/images/contact.png',
  founder: '/images/dinar-davis.png',
};

export const services = [
  {
    id: 'bookkeeping',
    title: 'Bookkeeping',
    tagline: 'Stay current.',
    items: [
      'Transaction categorization',
      'Bank and credit-card reconciliation',
      'Monthly financial statements',
      'Profit & Loss and Balance Sheet',
      'Consistent monthly close',
    ],
  },
  {
    id: 'cleanup',
    title: 'Cleanup + Catch-Up',
    tagline: 'Get current.',
    items: [
      'Review and cleanup of existing books',
      'Account reconciliation',
      'Transaction corrections',
      'Resolution of outstanding discrepancies',
      'Clean starting point for ongoing bookkeeping',
    ],
  },
  {
    id: 'setup',
    title: 'QuickBooks Setup',
    tagline: 'Start clean.',
    items: [
      'QuickBooks Online setup',
      'Customized chart of accounts',
      'Bank and credit-card connections',
      'Opening balances',
      'Account walkthrough',
    ],
  },
  {
    id: 'training',
    title: 'QuickBooks Training',
    tagline: 'Do it yourself. Do it right.',
    items: [
      'One-on-one QBO training',
      'Work inside your own books',
      'Learn your monthly workflow',
      'Get answers to your questions',
      'Simple bookkeeping checklist',
    ],
  },
  {
    id: 'payroll',
    title: 'Payroll',
    tagline: 'Stay on time.',
    items: [
      'Regular payroll processing',
      'Employee additions and changes',
      'Earnings, deduction, and withholding updates',
      'PTO and payroll adjustments',
      'Payroll reconciliation',
      'Payroll reports and recordkeeping',
      'ADP and Paychex experience',
    ],
  },
];

export const industries = [
  {
    name: 'Consultants',
    image: '/images/consultants.png',
    description:
      'Keep track of client income, business expenses, and monthly financials without adding more administration to your workload.',
  },
  {
    name: 'Agencies',
    image: '/images/agencies.png',
    description:
      'Stay on top of revenue, expenses, team costs, and payroll as projects and clients grow and evolve.',
  },
  {
    name: 'Solopreneurs',
    image: '/images/solopreneurs.png',
    description:
      'Ensure business and personal finances are separate, records stay current, and monthly results are easy to understand.',
  },
  {
    name: 'Nonprofits',
    image: '/images/nonprofits.png',
    description:
      'Get support with tracking funding, invoicing, and the day-to-day financial details, so that you can put more energy toward your mission.',
  },
  {
    name: 'Service Businesses',
    image: '/images/service-businesses.png',
    description:
      'Spend more time serving your customers and less time managing the books, with income, expenses, payroll, and monthly financials handled along the way.',
  },
];

export const projectPlans = [
  {
    id: 'setup',
    name: 'QuickBooks Setup',
    price: '$250',
    unit: 'one-time',
    copy: 'Get QuickBooks Online set up correctly from the start.',
    items: [
      'QuickBooks Online setup',
      'Customized chart of accounts',
      'Bank and credit-card feed connections',
      'Opening balances',
      '30-minute account walkthrough',
    ],
  },
  {
    id: 'cleanup',
    name: 'Cleanup + Catch-Up',
    priceLead: 'Starting at',
    price: '$100',
    unit: '/each month behind',
    flushUnit: true,
    copy: 'Bring overdue or disorganized books current and get a clean starting point.',
    items: [
      'Review of existing books',
      'Transaction cleanup',
      'Account reconciliation',
      'Corrections and adjustments',
      'Clean financial statements',
    ],
    note: 'Final pricing is provided after a review of your books.',
  },
];

export const monthlyPlans = {
  essential: {
    id: 'essential',
    name: 'Essential',
    price: '$300',
    unit: '/month',
    copy: 'Professional monthly bookkeeping and reporting for straightforward service businesses and independent professionals.',
    items: [
      'Monthly transaction categorization',
      'Bank and credit-card reconciliation',
      'Profit & Loss statement',
      'Balance Sheet',
      'Monthly financial close',
      'Timely monthly financial statements',
    ],
  },
  custom: {
    id: 'custom',
    name: 'Custom',
    price: 'Custom pricing',
    priceText: true,
    unit: '',
    copy: 'For businesses with more accounts, transactions, entities, or bookkeeping complexity.',
    detail:
      "We'll review your books, define what needs to be handled, and provide a clear monthly price before work begins.",
    items: [
      'Everything in the Essential package, with the scope adjusted to fit your business needs.',
    ],
  },
};

export const trainingPlan = {
  id: 'training',
  name: 'QuickBooks Training',
  price: '$125',
  unit: '/session',
  flushUnit: true,
  copy: 'One-on-one QuickBooks Online training built around your business, your books, and what you need help with.',
  items: [
    'Private 60-minute video session',
    'Training in your own QuickBooks account',
    'Guidance based on your business and workflow',
    'Questions answered as we work',
    'Simple monthly bookkeeping checklist',
  ],
};

export const payrollTiers = [
  { min: 1, max: 5, price: '$150' },
  { min: 6, max: 10, price: '$200' },
  { min: 11, max: 20, price: '$275' },
  { min: 21, max: 30, price: '$350' },
  { min: 31, max: 40, price: '$425' },
  { min: 41, max: 50, price: '$500' },
];

export const payrollIncludes = [
  'Regular payroll processing',
  'Employee additions and changes',
  'Earnings, deductions, and withholding updates',
  'PTO and payroll adjustments',
  'Payroll reconciliation',
  'Payroll reports and recordkeeping',
  'ADP and Paychex experience',
];

export const faqs = [
  {
    question: 'Why hire a bookkeeper if I already use QuickBooks?',
    answer:
      'QuickBooks keeps the records. Bookkeeping keeps those records accurate. Rime categorizes transactions, reconciles accounts, closes the books, and prepares your monthly financial statements so the information in QuickBooks reflects what actually happened in your business.',
  },
  {
    question: 'What exactly do you handle every month?',
    answer:
      'For Essential clients, Rime handles transaction categorization, bank and credit-card reconciliations, your monthly close, and preparation of your Profit & Loss statement and Balance Sheet. Financials are delivered on a set monthly schedule. Businesses that need additional support can be handled under a custom monthly scope.',
  },
  {
    question: 'What if my books are already behind?',
    answer:
      "That's what Cleanup + Catch-Up is for. Rime reviews your existing books, reconciles accounts, corrects transactions and outstanding discrepancies, and brings the books current. Pricing starts at $100 for each month behind, with final pricing provided after a review.",
  },
  {
    question: 'Do you prepare taxes?',
    answer:
      'No. Rime provides accurate bookkeeping and financial records for your CPA or tax preparer, but does not prepare or file income-tax returns.',
  },
  {
    question: 'Will you work with my CPA or tax preparer?',
    answer:
      'Yes. Rime can provide organized financial statements and records and work with your CPA or tax preparer to resolve bookkeeping questions when needed.',
  },
  {
    question: 'Do you offer payroll?',
    answer:
      'Yes. Rime provides managed payroll through your existing payroll platform for teams of up to 50 people. Pricing starts at $150 per month based on team size and payroll needs.',
  },
  {
    question: 'Can you teach me to manage my own QuickBooks?',
    answer:
      'Yes. Rime offers one-on-one QuickBooks Online training for business owners who prefer to handle their own books. Sessions take place inside your QuickBooks account and focus on your business, your workflow, and the questions you need answered.',
  },
  {
    question: 'How is my financial information protected?',
    answer:
      'Rime uses secure platform access and individual user permissions whenever available rather than requesting or storing your primary banking credentials. Access is limited to the information needed to provide your services.',
  },
  {
    question: 'Do I have to sign a long-term contract?',
    answer:
      'No. Standard monthly bookkeeping and payroll services do not require a long-term commitment. Your scope, pricing, and service terms are documented before work begins.',
  },
];
